/**
 * Parsing and typing for realtime `Invalidate` frames shared by the backend
 * WS channels (assistant-api, auth-api).
 *
 * The gateways broadcast lightweight cache-invalidation signals —
 * `{ command: 'Invalidate', entity, action, id?, ts }` — whenever backend
 * data changes. Frames carry no entity data; consumers map them to RTK Query
 * tag invalidations so subscribed queries refetch over authorized HTTP.
 */

export interface InvalidateMessage {
  command: 'Invalidate';
  /**
   * Deliberately `string`: the backend may start emitting entities this
   * client version doesn't know yet, and parsing must stay
   * forward-compatible — consumers map unknown values to no-ops rather than
   * rejecting the frame. Each channel package documents its known values.
   */
  entity: string;
  /** `string` for the same reason as `entity`. */
  action: string;
  id?: number | string;
  /** Server-side emission time (ISO); used for deduplicating event bursts. */
  ts: string;
}

const isInvalidateMessage = (value: unknown): value is InvalidateMessage => {
  if (typeof value !== 'object' || value === null) return false;

  return (
    'command' in value &&
    value.command === 'Invalidate' &&
    'entity' in value &&
    typeof value.entity === 'string' &&
    'action' in value &&
    typeof value.action === 'string'
  );
};

/**
 * Parses a raw WS `onMessage` payload into an {@link InvalidateMessage}, or
 * returns `null` for any other frame (auth acks, pongs, malformed JSON).
 *
 * A missing or non-string `ts` is normalized to `''` rather than rejecting
 * the frame — deduplication then falls back to the client clock
 * (`Date.parse('')` is `NaN`), so the invalidation itself is never lost.
 */
export const parseInvalidateMessage = (payload: { message: string }): InvalidateMessage | null => {
  try {
    const json: unknown = JSON.parse(payload.message);

    if (!isInvalidateMessage(json)) return null;

    return typeof json.ts === 'string' ? json : { ...json, ts: '' };
  } catch {
    return null;
  }
};
